import { useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { getContractErrorMessage } from "@/lib/contractErrorMessages";

const DOC_TYPE_LABELS: Record<string, string> = {
  certificacion: "Certificación",
  medicion: "Medición",
  acta_cliente: "Acta de cliente",
  factura_borrador: "Factura borrador",
  correo_aceptacion: "Correo de aceptación",
  generated_pdf: "PDF generado", sent_pdf: "PDF enviado", signed_received: "Firmado recibido", external_evidence: "Evidencia externa", acceptance_evidence: "Evidencia de aceptación", otro: "Otro documento",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
};
const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

function inferMimeType(filename: string): string | null {
  const extension = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return MIME_BY_EXTENSION[extension] ?? null;
}

interface Props {
  certificationId: number;
  contractId: number;
  canManageDocs: boolean;
  revisionId?: number | null;
  initialDocumentType?: string;
  onDocumentMutated?: (certificationUpdatedAt?: string) => void;
}

export function CertificationDocumentsPanel({ certificationId, contractId, canManageDocs, revisionId, initialDocumentType, onDocumentMutated }: Props) {
  const utils = trpc.useUtils();
  const docsQuery = trpc.contractCertificationDocumentsList.useQuery(
    { certificationId, contractId },
    { enabled: !!certificationId },
  );
  const uploadMut = trpc.contractCertificationDocumentUpload.useMutation();
  const deleteMut = trpc.contractCertificationDocumentDelete.useMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<string>(initialDocumentType ?? "certificacion");
  const [uploading, setUploading] = useState(false);

  async function invalidate() {
    await Promise.all([
      utils.contractCertificationDocumentsList.invalidate({ certificationId, contractId }),
      utils.contractCertificationGet.invalidate({ certificationId, contractId }),
    ]);
    // Invalidate the cert itself so the parent dialog picks up a fresh
    // updatedAt. Without this, a document mutation followed by a cert edit
    // would use a stale expectedUpdatedAt and fail with CERT_STALE_UPDATE if
    // the upload updated the cert row (e.g. via a DB trigger).
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      if (file.size <= 0 || file.size > MAX_DOCUMENT_BYTES) {
        throw new Error("El archivo debe ocupar entre 1 byte y 20 MB.");
      }
      if (file.name.trim().split(".").length !== 2) {
        throw new Error("El nombre debe contener una sola extensión.");
      }
      const browserMime = file.type.trim().toLowerCase();
      const inferredMime = inferMimeType(file.name);
      const mimeType = browserMime && browserMime !== "application/octet-stream" ? browserMime : inferredMime;
      if (!mimeType) {
        throw new Error("No se puede determinar un tipo MIME permitido para este archivo.");
      }
      if (mimeType !== inferredMime) {
        throw new Error("El nombre y el tipo MIME del archivo no coinciden.");
      }
      const reader = new FileReader();
      const fileData = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1] ?? "");
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const uploadRes = await uploadMut.mutateAsync({
        certificationId,
        contractId,
        documentType: docType as any,
        ...(revisionId ? { revisionId } : {}),
        originalFilename: file.name,
        fileData,
        mimeType,
      });
      toast.success("Documento subido.");
      await invalidate();
      if (uploadRes?.certificationUpdatedAt && onDocumentMutated) {
        onDocumentMutated(uploadRes.certificationUpdatedAt);
      }
    } catch (err: any) {
      toast.error(getContractErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(documentId: number) {
    try {
      const deleteRes = await deleteMut.mutateAsync({ documentId, contractId });
      toast.success("Documento eliminado.");
      await invalidate();
      if (deleteRes?.certificationUpdatedAt && onDocumentMutated) {
        onDocumentMutated(deleteRes.certificationUpdatedAt);
      }
    } catch (err: any) {
      toast.error(getContractErrorMessage(err));
    }
  }

  const docs = docsQuery.data ?? [];

  return (
    <div className="min-w-0 max-w-full space-y-3">
      {docsQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-3">
          No hay documentos adjuntos.
        </p>
      ) : (
        <ul className="min-w-0 max-w-full space-y-1">
          {docs.map((doc: any) => (
            <li key={doc.id} className="flex min-w-0 max-w-full flex-col gap-2 rounded border px-3 py-2 text-sm sm:flex-row sm:items-center">
              <FileText className="w-4 h-4 shrink-0 self-start text-muted-foreground sm:self-center" />
              <div className="min-w-0 w-full flex-1 sm:w-auto">
                <a
                  href={doc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={doc.originalFilename ?? "documento"}
                  className="block min-w-0 max-w-full truncate font-medium hover:underline"
                >
                  {doc.originalFilename ?? "documento"}
                </a>
                <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-1 break-words text-xs text-muted-foreground">
                  <span>{DOC_TYPE_LABELS[doc.documentType] ?? "Otro documento"}</span>
                  {doc.fileSize != null && <span>{formatBytes(Number(doc.fileSize))}</span>}
                </div>
              </div>
              {canManageDocs && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 h-7 w-7 text-destructive"
                  onClick={() => handleDelete(doc.id)}
                  disabled={deleteMut.isPending}
                  title="Eliminar documento"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManageDocs && (
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={docType} onValueChange={setDocType} disabled={Boolean(initialDocumentType)}>
            <SelectTrigger className="h-8 w-full min-w-0 text-xs sm:w-48">
              <SelectValue placeholder="Tipo de documento" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DOC_TYPE_LABELS).map(([val, label]) => (
                <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-full text-xs sm:w-auto sm:shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="w-3 h-3 mr-1" />
            {uploading ? "Subiendo…" : "Subir archivo"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
          />
        </div>
      )}
    </div>
  );
}
